import React from 'react';
import { CheckCircle2 } from 'lucide-react';

const steps = [
    {
        number: 1,
        title: 'Search',
        description: 'Choose your favorite room',
        id: 'search'
    },
    {
        number: 2,
        title: 'Booking',
        description: 'Enter your booking details',
        id: 'booking'
    },
    {
        number: 3,
        title: 'Checkout',
        description: 'Use your preferred payment method',
        id: 'checkout'
    },
    {
        number: 4,
        title: 'Confirmation',
        description: 'Receive a confirmation email',
        id: 'confirmation'
    }
];

function ProgressBar({ currentStep }) {
    const currentStepIndex = steps.findIndex(step => step.id === currentStep);

    const isCompleted = (stepIndex) => stepIndex < currentStepIndex;
    const isActive = (stepIndex) => stepIndex === currentStepIndex;

    return (
        <div className="w-full bg-white shadow-md py-6 md:py-8 lg:py-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Progress Bar Container */}
                <div className="relative">
                    {/* Steps */}
                    <div className="flex justify-between items-start md:items-center relative z-10">
                        {steps.map((step, index) => (
                            <div
                                key={step.id}
                                className="flex-1 flex flex-col items-center"
                            >
                                {/* Step Circle */}
                                <div className="flex flex-col items-center mb-2 md:mb-4">
                                    {isCompleted(index) ? (
                                        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-green-600 text-white shadow-lg transform transition-all">
                                            <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7" />
                                        </div>
                                    ) : (
                                        <div
                                            className={`flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full font-bold text-sm md:text-base shadow-lg transform transition-all duration-300 ${isActive(index)
                                                ? 'bg-green-600 text-white ring-4 ring-green-300 scale-110'
                                                : 'bg-gray-300 text-gray-700'
                                                }`}
                                        >
                                            {step.number}
                                        </div>
                                    )}
                                </div>

                                {/* Step Title and Description */}
                                <div className="text-center">
                                    <p
                                        className={`text-xs md:text-sm font-semibold transition-colors duration-300 ${isActive(index) || isCompleted(index)
                                            ? 'text-green-700'
                                            : 'text-gray-600'
                                            }`}
                                    >
                                        {step.title}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5 md:mt-1 hidden sm:block">
                                        {step.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Connecting Lines */}
                    <div className="absolute top-5 md:top-6 left-0 right-0 flex items-center justify-between px-8 md:px-12 z-0 pointer-events-none">
                        {steps.map((step, index) => {
                            if (index === steps.length - 1) return null;

                            return (
                                <div
                                    key={`line-${step.id}`}
                                    className="flex-1 h-1 md:h-1.5 rounded-full transition-all duration-500"
                                    style={{
                                        backgroundColor: isCompleted(index + 1) ? '#16a34a' : '#d1d5db',
                                        marginLeft: index > 0 ? '0' : '0',
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>

                {/* Mobile Step Description */}
                <div className="mt-6 sm:hidden text-center">
                    {currentStep && (
                        <p className="text-xs text-gray-600 leading-relaxed px-2">
                            {steps.find(s => s.id === currentStep)?.description}
                        </p>
                    )}
                </div>

                {/* Progress Indicator Bar (Optional - Shows percentage) */}
                <div className="mt-6 md:mt-8">
                    <div className="w-full bg-gray-200 rounded-full h-1.5 md:h-2 overflow-hidden shadow-sm">
                        <div
                            className="bg-gradient-to-r from-green-500 to-green-600 h-full rounded-full transition-all duration-500 ease-out"
                            style={{
                                width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
                            }}
                        />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-600">
                            Step {currentStepIndex + 1} of {steps.length}
                        </span>
                        <span className="text-xs font-semibold text-green-700">
                            {Math.round(((currentStepIndex + 1) / steps.length) * 100)}% Complete
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProgressBar;
